import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * TokenService - Verwaltet alle Token-Operationen
 * Bricht die zirkuläre Abhängigkeit zwischen jwtInterceptor und AuthService
 * 
 * Verwendet BehaviorSubject für Observable Token-Updates
 * Speichert Token mit Ablaufdatum in SessionStorage
 */
@Injectable({
  providedIn: 'root'
})
export class TokenService {
  // BehaviorSubject für Token-Änderungen
  private tokenSubject = new BehaviorSubject<string | null>(this.getStoredToken());
  public token$: Observable<string | null> = this.tokenSubject.asObservable();

  constructor() {
    // Token beim Service-Konstruktion laden
    const storedToken = this.getStoredToken();
    if (storedToken) {
      this.tokenSubject.next(storedToken);
    }
  }

  /**
   * Token im SessionStorage speichern mit Ablaufdatum (2 Stunden Inaktivität)
   */
  setToken(token: string): void {
    const expiresAt = Date.now() + (2 * 60 * 60 * 1000); // 2 Stunden in Millisekunden
    const tokenData = { token, expiresAt };
    sessionStorage.setItem('authToken', JSON.stringify(tokenData));
    this.tokenSubject.next(token);
  }

  /**
   * Token aus SessionStorage abrufen (nur wenn nicht abgelaufen)
   */
  getToken(): string | null {
    return this.getStoredToken();
  }

  /**
   * Token-Ablaufdatum verlängern
   */
  refreshTokenExpiry(): void {
    const token = this.getStoredToken();
    if (token) {
      this.setToken(token); // Setzt neues Ablaufdatum
    }
  }

  /**
   * Token löschen
   */
  clearToken(): void {
    sessionStorage.removeItem('authToken');
    this.tokenSubject.next(null);
  }

  /**
   * Token aus SessionStorage abrufen (privat) - mit Ablaufdatum-Validierung
   */
  private getStoredToken(): string | null {
    const tokenDataStr = sessionStorage.getItem('authToken');
    if (!tokenDataStr) return null;
    
    try {
      const tokenData = JSON.parse(tokenDataStr);
      // Prüfe ob Token abgelaufen ist
      if (Date.now() > tokenData.expiresAt) {
        this.clearToken();
        return null;
      }
      return tokenData.token;
    } catch {
      this.clearToken();
      return null;
    }
  }
}
